/**
 * Hook del detalle de una Flota (US-2.3). La red vive en `fleetApi`; aquí solo el estado
 * de la vista (loading / no encontrado / error / refresh), separable de la presentación.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Fleet } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { fleetApi } from '../api/fleetApi';

export interface UseFleetResult {
  fleet: Fleet | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (flota inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la flota.';
}

export function useFleet(fleetId: string | undefined): UseFleetResult {
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!fleetId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    fleetApi
      .get(fleetId)
      .then((result) => {
        if (!cancelled) setFleet(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fleetId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { fleet, loading, error, notFound, refresh };
}
